import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
    History, ChevronUp, ChevronDown, Check, X, Clock,
    Car, Receipt, Utensils, Paperclip, ExternalLink, Loader2,
} from 'lucide-react';
import {
    getMyTimesheetSubmissions,
    getExpenseHistory,
    getExpensesV2,
} from '../lib/api';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const RECEIPT_TYPE_LABELS = {
    travel_flight: 'Flight', travel_train: 'Train/Bus', travel_taxi: 'Taxi',
    travel_rental: 'Rental car', travel_fuel: 'Fuel', travel_parking: 'Parking',
    travel_tolls: 'Tolls', accommodation: 'Accommodation',
    meals_client: 'Meal (client)', meals_business: 'Meal (business)',
    office_supplies: 'Office supplies', software: 'Software',
    equipment: 'Equipment', conference: 'Conference',
    phone_internet: 'Phone/Internet', other: 'Other',
};

const ALLOWANCE_TYPE_LABELS = {
    domestic_day: 'Domestic day',
    domestic_night: 'Domestic overnight',
    international_day: 'International day',
    international_night: 'International overnight',
};

const VEHICLE_TYPE_LABELS = {
    own_car: 'Own car',
    company_car: 'Company car',
    rental: 'Rental',
    motorcycle: 'Motorcycle',
    bicycle: 'Bicycle',
    other: 'Other',
};

const PAYMENT_METHOD_LABELS = {
    personal_card: 'Personal card',
    company_card: 'Company card',
    cash: 'Cash',
};

function formatDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/**
 * Authenticated fetch + window.open for Phase-2 disk-based receipts.
 * Falls back to data URL open for legacy receipt_data records.
 * Mirrors the pattern in day-receipt-form.jsx::handleViewFile.
 */
async function openReceiptFile(expense, setViewingId, t) {
    if (expense.receipt_path) {
        setViewingId(expense.id);
        try {
            const { default: api } = await import('../lib/api');
            const response = await api.get(`/expenses/receipt/${expense.receipt_path}`, {
                responseType: 'blob',
            });
            const blobUrl = URL.createObjectURL(response.data);
            const win = window.open(blobUrl, '_blank');
            if (win) {
                setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
            } else {
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = expense.receipt_filename || expense.receipt_path.split('/').pop() || 'receipt';
                a.click();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            }
        } catch {
            toast.error(t('expenses.receipt.view_error'));
        } finally {
            setViewingId(null);
        }
    } else if (expense.receipt_data) {
        // Legacy base64: if it's already a data URL, open directly; otherwise
        // construct one (best-guess MIME from filename).
        const data = expense.receipt_data;
        const src = data.startsWith('data:')
            ? data
            : `data:${expense.receipt_filename?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'};base64,${data}`;
        window.open(src, '_blank');
    }
}

function getStatusIcon(status) {
    switch (status) {
        case 'approved': return <Check className="w-4 h-4 text-green-600" />;
        case 'rejected': return <X className="w-4 h-4 text-red-600" />;
        default: return <Clock className="w-4 h-4 text-amber-600" />;
    }
}

function getStatusColor(status) {
    switch (status) {
        case 'approved': return 'bg-green-50 text-green-800 border-green-200';
        case 'rejected': return 'bg-red-50 text-red-800 border-red-200';
        default: return 'bg-amber-50 text-amber-800 border-amber-200';
    }
}

/**
 * Rich per-expense detail row used in the Expense Reports drill-down.
 * Shows every user-filled field for its category so employees can verify
 * what was submitted and download attached receipts.
 */
function ExpenseDetailRow({ expense: e, viewingReceiptId, setViewingReceiptId }) {
    const { t } = useTranslation();
    const category = e.expense_category;
    const displayAmount = e.calculated_amount ?? e.amount ?? 0;
    const currency = category === 'receipt' ? (e.currency || 'SEK') : 'SEK';
    const hasAttachment = !!(e.receipt_data || e.receipt_path);
    const isViewing = viewingReceiptId === e.id;

    const icon = category === 'mileage'
        ? <Car className="w-5 h-5 text-blue-600" />
        : category === 'receipt'
            ? <Receipt className="w-5 h-5 text-emerald-600" />
            : <Utensils className="w-5 h-5 text-amber-600" />;

    const title = category === 'mileage'
        ? `${e.from_location || '—'} → ${e.to_location || '—'}`
        : category === 'receipt'
            ? (e.description || RECEIPT_TYPE_LABELS[e.receipt_type] || 'Receipt')
            : `${e.destination || '—'} · ${ALLOWANCE_TYPE_LABELS[e.allowance_type] || e.allowance_type || ''}`;

    // Inline metadata chips (compact, comma-separated feel)
    const chips = [];
    chips.push(e.date);
    if (e.project_name) chips.push(e.project_name);
    if (category === 'mileage') {
        if (e.distance_mil != null) chips.push(`${e.distance_mil} mil`);
        if (e.vehicle_type) chips.push(VEHICLE_TYPE_LABELS[e.vehicle_type] || e.vehicle_type);
        if (e.round_trip) chips.push('round trip');
    } else if (category === 'receipt') {
        if (e.receipt_type && e.description) {
            // description was used as the title; show type as chip
            chips.push(RECEIPT_TYPE_LABELS[e.receipt_type] || e.receipt_type);
        }
        if (e.payment_method) chips.push(PAYMENT_METHOD_LABELS[e.payment_method] || e.payment_method);
    } else if (category === 'allowance') {
        if (e.country) chips.push(e.country);
        if (e.start_datetime) chips.push(`${formatDateTime(e.start_datetime)} → ${formatDateTime(e.end_datetime)}`);
        if (e.nights) chips.push(`${e.nights} night${e.nights === 1 ? '' : 's'}`);
        const mealsProvided = [];
        if (e.breakfast_provided) mealsProvided.push('breakfast');
        if (e.lunch_provided) mealsProvided.push('lunch');
        if (e.dinner_provided) mealsProvided.push('dinner');
        if (mealsProvided.length > 0) chips.push(`provided: ${mealsProvided.join(', ')}`);
    }
    if (e.billable) chips.push('billable');

    return (
        <div className="p-3 bg-background rounded-lg border space-y-2">
            {/* Header row — icon + title + amount */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="mt-0.5 shrink-0">{icon}</div>
                    <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm break-words">{title}</p>
                        {chips.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {chips.join(' · ')}
                            </p>
                        )}
                    </div>
                </div>
                <span className="font-mono font-bold text-sm whitespace-nowrap">
                    {displayAmount.toFixed(0)} {currency}
                </span>
            </div>

            {/* Notes — only if present */}
            {e.notes && (
                <p className="text-xs text-muted-foreground italic pl-8">{e.notes}</p>
            )}

            {/* Receipt attachment — filename + View button (auth'd download) */}
            {category === 'receipt' && hasAttachment && (
                <div className="flex items-center gap-2 pl-8">
                    <Paperclip className="w-3 h-3 text-blue-600 shrink-0" />
                    <span className="text-xs text-muted-foreground truncate flex-1">
                        {e.receipt_filename || (e.receipt_path ? e.receipt_path.split('/').pop() : 'Attachment')}
                    </span>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => openReceiptFile(e, setViewingReceiptId, t)}
                        disabled={isViewing}
                        aria-label={t('common.view')}
                    >
                        {isViewing ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                            <ExternalLink className="w-3 h-3 mr-1" />
                        )}
                        {isViewing ? t('common.processing') : t('common.view')}
                    </Button>
                </div>
            )}
        </div>
    );
}

function ReviewBanner({ reviewedAt, reviewerName, status, comment }) {
    const { t } = useTranslation();
    if (!reviewedAt) return null;
    const isApproved = status === 'approved';
    return (
        <div className={`px-4 py-2 text-sm ${isApproved ? 'bg-green-50' : status === 'rejected' ? 'bg-red-50' : ''}`}>
            <span className="font-medium">
                {isApproved
                    ? t('submission_history.approved_by', { name: reviewerName || 'admin' })
                    : t('submission_history.rejected_by', { name: reviewerName || 'admin' })}
            </span>
            <span className="text-muted-foreground ml-2">
                {t('submission_history.on_date', { date: new Date(reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })}
            </span>
            {comment && (
                <p className="text-muted-foreground mt-1 italic">&ldquo;{comment}&rdquo;</p>
            )}
        </div>
    );
}

/**
 * Collapsed-by-default submission history section for LogReports.
 * Two tabs: Timesheets (time submissions) + Expense Reports (expense submissions
 * with per-expense drill-down). Each backed by its own endpoint:
 *   - GET /api/timesheets/my-submissions
 *   - GET /api/expenses/history
 *
 * Data fetched lazily on first expand to avoid unnecessary load on users
 * who never open the section.
 */
export default function SubmissionHistorySection() {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('time');
    const [timeHistory, setTimeHistory] = useState([]);
    const [expenseHistory, setExpenseHistory] = useState([]);
    const [expandedExpenseReport, setExpandedExpenseReport] = useState(null);
    const [drilldownExpenses, setDrilldownExpenses] = useState([]);
    const [loadingDrilldown, setLoadingDrilldown] = useState(false);
    const [viewingReceiptId, setViewingReceiptId] = useState(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [timeRes, expRes] = await Promise.all([
                getMyTimesheetSubmissions(),
                getExpenseHistory(),
            ]);
            setTimeHistory(timeRes.data || []);
            setExpenseHistory(expRes.data || []);
            setLoaded(true);
        } catch {
            toast.error(t('submission_history.load_error'));
        } finally {
            setLoading(false);
        }
    };

    const toggleExpanded = () => {
        const next = !expanded;
        setExpanded(next);
        if (next && !loaded && !loading) {
            fetchAll();
        }
    };

    const toggleDrilldown = async (report) => {
        if (expandedExpenseReport?.month === report.month && expandedExpenseReport?.year === report.year) {
            setExpandedExpenseReport(null);
            setDrilldownExpenses([]);
            return;
        }
        setExpandedExpenseReport(report);
        setLoadingDrilldown(true);
        try {
            const res = await getExpensesV2({ month: report.month, year: report.year });
            setDrilldownExpenses(res.data || []);
        } catch {
            toast.error(t('submission_history.expense_details_error'));
        } finally {
            setLoadingDrilldown(false);
        }
    };

    return (
        <Card>
            {/* Collapsible header */}
            <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30"
                onClick={toggleExpanded}
                data-testid="submission-history-toggle"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <History className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                        <h3 className="font-heading font-bold text-lg">{t('submission_history.section_title')}</h3>
                        <p className="text-sm text-muted-foreground">{t('submission_history.section_subtitle')}</p>
                    </div>
                </div>
                {expanded ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
            </div>

            {expanded && (
                <CardContent className="pt-0 border-t">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="w-6 h-6 border-2 border-foreground border-t-transparent animate-spin rounded-full" />
                        </div>
                    ) : (
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="pt-4">
                            <TabsList className="grid w-full grid-cols-2 max-w-md">
                                <TabsTrigger value="time" data-testid="tab-history-time">
                                    <Clock className="w-4 h-4 mr-2" />
                                    {t('submission_history.timesheets_tab')} {timeHistory.length > 0 && `(${timeHistory.length})`}
                                </TabsTrigger>
                                <TabsTrigger value="expenses" data-testid="tab-history-expenses">
                                    <Receipt className="w-4 h-4 mr-2" />
                                    {t('submission_history.expenses_tab')} {expenseHistory.length > 0 && `(${expenseHistory.length})`}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="time" className="mt-4">
                                {timeHistory.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                        <p className="text-muted-foreground">{t('submission_history.no_timesheets')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {timeHistory.map((ts) => (
                                            <div
                                                key={`${ts.year}-${ts.month}`}
                                                className="border rounded-lg overflow-hidden"
                                                data-testid={`history-timesheet-${ts.year}-${ts.month}`}
                                            >
                                                <div className="p-4 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                            ts.status === 'approved' ? 'bg-green-100' :
                                                                ts.status === 'rejected' ? 'bg-red-100' : 'bg-amber-100'
                                                        }`}>
                                                            {getStatusIcon(ts.status)}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">
                                                                {MONTH_NAMES[ts.month - 1]} {ts.year}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {ts.entries_count} {ts.entries_count === 1 ? 'entry' : 'entries'}
                                                                {ts.submitted_at && ` • Submitted ${new Date(ts.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <p className="text-lg font-bold font-mono">
                                                                {ts.total_hours?.toFixed(1) || '0.0'}h
                                                            </p>
                                                            {ts.total_miles > 0 && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {ts.total_miles.toFixed(1)} mil
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ts.status)}`}>
                                                            {ts.status?.charAt(0).toUpperCase() + ts.status?.slice(1)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <ReviewBanner
                                                    reviewedAt={ts.reviewed_at}
                                                    reviewerName={ts.reviewer_name}
                                                    status={ts.status}
                                                    comment={ts.rejection_comment}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="expenses" className="mt-4">
                                {expenseHistory.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                        <p className="text-muted-foreground">{t('submission_history.no_expenses')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {expenseHistory.map((rep) => {
                                            const isOpen = expandedExpenseReport?.month === rep.month && expandedExpenseReport?.year === rep.year;
                                            return (
                                                <div
                                                    key={`${rep.year}-${rep.month}`}
                                                    className={`border rounded-lg overflow-hidden ${isOpen ? 'ring-2 ring-primary' : ''}`}
                                                    data-testid={`history-expense-${rep.year}-${rep.month}`}
                                                >
                                                    <div
                                                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30"
                                                        onClick={() => toggleDrilldown(rep)}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                                rep.status === 'approved' ? 'bg-green-100' :
                                                                    rep.status === 'rejected' ? 'bg-red-100' : 'bg-amber-100'
                                                            }`}>
                                                                {getStatusIcon(rep.status)}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium">
                                                                    {MONTH_NAMES[rep.month - 1]} {rep.year}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {rep.items_count} {rep.items_count === 1 ? 'item' : 'items'}
                                                                    {rep.submitted_at && ` • Submitted ${new Date(rep.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <div className="text-right">
                                                                <p className="text-lg font-bold font-mono">
                                                                    {rep.grand_total?.toFixed(0)} kr
                                                                </p>
                                                                <div className="flex gap-2 text-xs text-muted-foreground justify-end">
                                                                    {rep.mileage_total > 0 && (
                                                                        <span className="flex items-center gap-1">
                                                                            <Car className="w-3 h-3 text-blue-500" />
                                                                            {rep.mileage_total.toFixed(0)}
                                                                        </span>
                                                                    )}
                                                                    {rep.receipts_total > 0 && (
                                                                        <span className="flex items-center gap-1">
                                                                            <Receipt className="w-3 h-3 text-emerald-500" />
                                                                            {rep.receipts_total.toFixed(0)}
                                                                        </span>
                                                                    )}
                                                                    {rep.allowances_total > 0 && (
                                                                        <span className="flex items-center gap-1">
                                                                            <Utensils className="w-3 h-3 text-amber-500" />
                                                                            {rep.allowances_total.toFixed(0)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(rep.status)}`}>
                                                                {rep.status?.charAt(0).toUpperCase() + rep.status?.slice(1)}
                                                            </span>
                                                            {isOpen ? (
                                                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isOpen && (
                                                        <div className="border-t bg-muted/20">
                                                            <ReviewBanner
                                                                reviewedAt={rep.reviewed_at}
                                                                reviewerName={rep.reviewer_name}
                                                                status={rep.status}
                                                                comment={rep.review_comment}
                                                            />
                                                            <div className="p-4">
                                                                {loadingDrilldown ? (
                                                                    <div className="flex items-center justify-center py-4">
                                                                        <div className="w-5 h-5 border-2 border-foreground border-t-transparent animate-spin rounded-full" />
                                                                    </div>
                                                                ) : drilldownExpenses.length === 0 ? (
                                                                    <p className="text-muted-foreground text-center py-4">{t('submission_history.no_expense_items')}</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {drilldownExpenses.map(e => (
                                                                            <ExpenseDetailRow
                                                                                key={e.id}
                                                                                expense={e}
                                                                                viewingReceiptId={viewingReceiptId}
                                                                                setViewingReceiptId={setViewingReceiptId}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    )}
                </CardContent>
            )}
        </Card>
    );
}
